import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, ClipboardList, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../config/constants";
import GlobalETicketsWidget from "../components/GlobalETicketsWidget";
import SEO from "../components/SEO";
import { useSiteImages } from "../hooks/useSiteImages";

export default function EventsPage() {
  const { getImage } = useSiteImages();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        console.error("Failed to load events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div data-testid="events-page" className="pt-20">
      <SEO title="Events" description="Premier youth track & field meets across the Southeast. Hosted by Coach Miami Presents. Register now for upcoming events." />
      <section className="py-24 bg-[#0F2137] relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-kicker">Coach Miami Presents</span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Premier Youth Track Meets</h1>
              <p className="text-xl text-gray-300 mb-8">For over 15 years, Coach Miami Presents has been hosting exceptionally organized youth track meets throughout the Southeast, providing young athletes an outstanding platform to showcase their talents.</p>
            </div>
            <div>
              <img src={getImage("events_coach_miami")} alt="Coach Miami Presents" className="rounded-xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">Upcoming</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">Events Calendar</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#D90429]" size={40} /></div>
          ) : events.length === 0 ? (
            <p className="text-center text-[#495057] py-12">No upcoming events at this time. Check back soon!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events.map((event) => (
                <motion.div key={event.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[#F8F9FA] rounded-xl overflow-hidden card-hover" data-testid={`event-card-${event.id}`}>
                  {event.image_url && <img src={event.image_url} alt={event.name} className="w-full h-auto" />}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-[#0F2137] mb-1">{event.name}</h3>
                    {event.host && <p className="text-[#D90429] font-medium mb-3">{event.host}</p>}
                    <div className="flex flex-wrap gap-4 text-sm mb-4">
                      <span className="flex items-center gap-2 text-[#0F2137]">
                        <Calendar className="text-[#D90429]" size={18} />
                        {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                      {event.register_url && (
                        <a href={event.register_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#D90429] hover:text-[#B00322] font-semibold transition-colors" data-testid={`register-link-${event.id}`}>
                          <ClipboardList size={18} />Register
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#0F2137] mb-3">
                      <MapPin className="text-[#D90429]" size={18} />{event.location}
                    </div>
                    {event.contact && (
                      <p className="text-sm text-[#495057]">
                        <strong>Contact:</strong>{" "}
                        {event.contact.includes('@') ? (
                          <a href={`mailto:${event.contact}`} className="text-[#D90429] hover:underline">{event.contact}</a>
                        ) : event.contact}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Shown only once a Global E Tickets embed URL is set in
          Admin -> Site Media -> Page Embeds. Hidden otherwise so the page
          never renders an empty ticket panel. */}
      {getImage("events_embed_url") && (
        <section className="py-24 bg-[#F8F9FA]">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="section-kicker">Coach Miami Presents</span>
              <h2 className="text-4xl font-bold text-[#0F2137]">Get Your Tickets</h2>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg" data-testid="coach-miami-ticket-widget">
              <GlobalETicketsWidget src={getImage("events_embed_url")} title="Upcoming events" maxWidth="420px" height={500} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
